/**
 * Flagged Content Page
 * View and manage flagged content across all children
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Flag, AlertTriangle, CheckCircle, Eye, Calendar, User, MessageSquare } from 'lucide-react';
import { childService, analyticsService, getErrorMessage } from '../services';

interface FlaggedItem {
  childId: string;
  childName: string;
  conversationId: string;
  subject: string | null;
  flagCount: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  date: string;
  preview: string;
}

export default function FlaggedContentPage() {
  const [flaggedItems, setFlaggedItems] = useState<FlaggedItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const childrenData = await childService.getChildren();

      // Fetch flagged content for each child
      const flagged: FlaggedItem[] = [];

      for (const child of childrenData) {
        try {
          // First get summary to check if there are flags
          const summary = await analyticsService.getChildSummary(child.id);
          const totalFlags = summary.flags?.total || 0;

          if (totalFlags > 0) {
            // Try to get flagged conversations for details
            try {
              const logs = await analyticsService.getChildLogs(child.id, { flagged: true });
              for (const conv of logs.conversations) {
                flagged.push({
                  childId: child.id,
                  childName: child.name,
                  conversationId: conv.id,
                  subject: conv.subject,
                  flagCount: conv.flagCount || 1,
                  severity: (conv.flagCount || 1) > 3 ? 'critical' : (conv.flagCount || 1) > 2 ? 'high' : (conv.flagCount || 1) > 1 ? 'medium' : 'low',
                  date: conv.createdAt,
                  preview: conv.messages?.[0]?.content ? conv.messages[0].content.slice(0, 100) + '...' : 'No preview available',
                });
              }
            } catch {
              // If we can't get conversation details, still show the flag count from summary
              const severity = summary.flags?.bySeverity;
              const topSeverity = (severity?.critical || 0) > 0 ? 'critical' : (severity?.high || 0) > 0 ? 'high' : (severity?.medium || 0) > 0 ? 'medium' : 'low';
              flagged.push({
                childId: child.id,
                childName: child.name,
                conversationId: '',
                subject: null,
                flagCount: totalFlags,
                severity: topSeverity as 'low' | 'medium' | 'high' | 'critical',
                date: new Date().toISOString(),
                preview: `${totalFlags} flagged item${totalFlags > 1 ? 's' : ''} detected by safety system`,
              });
            }
          }
        } catch {
          // Skip if no data available
        }
      }

      // Sort by date (newest first)
      flagged.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
      setFlaggedItems(flagged);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = flaggedItems.filter((item) => {
    if (selectedSeverity === 'all') return true;
    return item.severity === selectedSeverity;
  });

  const severityColors = {
    low: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    medium: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    high: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    critical: 'bg-red-200 text-red-900 dark:bg-red-800/50 dark:text-red-300',
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-kid-tangerine border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="mb-6">
        <Link
          to="/parent"
          className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>
      </div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
            <Flag className="w-6 h-6 text-red-500" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Flagged Content</h1>
            <p className="text-gray-600 dark:text-gray-400">
              Review content that was flagged by our safety system
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 text-red-700 dark:text-red-400 rounded-xl">
            {error}
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center">
            <p className="text-3xl font-bold text-gray-900 dark:text-white">{flaggedItems.length}</p>
            <p className="text-sm text-gray-500">Total Flagged</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center">
            <p className="text-3xl font-bold text-red-600">{flaggedItems.filter(i => i.severity === 'critical' || i.severity === 'high').length}</p>
            <p className="text-sm text-gray-500">High Priority</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center">
            <p className="text-3xl font-bold text-orange-600">{flaggedItems.filter(i => i.severity === 'medium').length}</p>
            <p className="text-sm text-gray-500">Medium Priority</p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow p-4 text-center">
            <p className="text-3xl font-bold text-yellow-600">{flaggedItems.filter(i => i.severity === 'low').length}</p>
            <p className="text-sm text-gray-500">Low Priority</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2 snap-x snap-mandatory">
          {['all', 'critical', 'high', 'medium', 'low'].map((severity) => (
            <button
              key={severity}
              onClick={() => setSelectedSeverity(severity)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors snap-start ${
                selectedSeverity === severity
                  ? 'bg-kid-tangerine text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {severity.charAt(0).toUpperCase() + severity.slice(1)}
            </button>
          ))}
        </div>

        {/* Flagged Items List */}
        {filteredItems.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-2xl shadow-lg">
            <CheckCircle className="w-16 h-16 mx-auto text-green-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              No Flagged Content
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              {selectedSeverity === 'all'
                ? 'Great news! No content has been flagged.'
                : `No ${selectedSeverity} priority items found.`}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredItems.map((item) => (
              <motion.div
                key={item.conversationId}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden border-l-4 border-l-red-500"
              >
                <div className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center flex-shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-500" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${severityColors[item.severity]}`}>
                            {item.severity.toUpperCase()}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {item.childName}
                          </span>
                          <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {item.subject || 'General'}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                          {item.preview}
                        </p>
                        <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(item.date)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Flag className="w-3 h-3" />
                            {item.flagCount} flags
                          </span>
                        </div>
                      </div>
                    </div>
                    <Link
                      to={`/parent/child/${item.childId}/conversations`}
                      className="flex items-center gap-1 px-4 py-2.5 text-sm bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex-shrink-0 touch-slop"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Safety Info */}
        <div className="mt-8 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-2xl p-6">
          <h3 className="font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
            <Flag className="w-5 h-5 text-kid-violet" />
            About Flagged Content
          </h3>
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <p>Our AI safety system automatically flags potentially inappropriate content:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Critical/High:</strong> Content that may require immediate attention</li>
              <li><strong>Medium:</strong> Content that may need review</li>
              <li><strong>Low:</strong> Minor concerns, usually false positives</li>
            </ul>
            <p className="mt-3">
              The AI tutor automatically redirects children away from inappropriate topics.
              Review flagged items to understand what your child is curious about.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
